import { Auth } from '@aws-amplify/auth';
import * as Yup from 'yup';
import { makeFormFieldCreator } from '@/components/Inputs/FieldCreator';

/** Cognito sends the code to the new email and keeps the old one until it is confirmed. */
export const sendEmailCode = async (email: string) => {
  const cognitoUser = await Auth.currentAuthenticatedUser();
  await Auth.updateUserAttributes(cognitoUser, { email });
};

export const cognitoHasConfirmedEmail = async (
  email: string
): Promise<boolean> => {
  try {
    const info = await Auth.currentUserInfo();
    const current = info?.attributes?.email;
    return (
      typeof current === 'string' &&
      current.trim().toLowerCase() === email.trim().toLowerCase()
    );
  } catch {
    return false;
  }
};

/** Signed in with Google: the email of that identity is Google's and takes no code. */
export const isGoogleSession = async (): Promise<boolean> => {
  try {
    const cognitoUser = await Auth.currentAuthenticatedUser();
    return /^google_/i.test(cognitoUser?.getUsername() ?? '');
  } catch {
    return false;
  }
};

const COGNITO_CODE_MESSAGES: Record<string, string> = {
  CodeMismatchException: 'El código no es correcto.',
  ExpiredCodeException: 'El código venció. Pedí uno nuevo.',
  LimitExceededException:
    'Hiciste muchos intentos. Esperá un rato y probá de nuevo.',
};

/** What to show for a Cognito error while asking for or typing the code; null if unknown. */
export const cognitoCodeErrorMessage = (error: unknown): string | null =>
  COGNITO_CODE_MESSAGES[(error as { code?: string } | null)?.code ?? ''] ??
  null;

export type CodeValues = { code: string };

export const codeFormFields = [
  makeFormFieldCreator<CodeValues>()({
    name: 'code',
    label: 'Código',
    inputProps: { inputMode: 'numeric', autoComplete: 'one-time-code' },
  }),
];

export const codeFormSchema = Yup.object().shape({
  code: Yup.string()
    .matches(/^\d{6}$/, 'El código tiene 6 números')
    .required('Campo obligatorio'),
});
